"use strict";
const EventEmitter = require("events").EventEmitter;
const Renderer = require("./LocalVeilGLRenderer");
const _ = require("lodash");

class LocalVeilGLComponent extends EventEmitter {
    /**
     * @member title {String|HTMLElement} - Get/set the visual title of the veil.
     * @member content {String|HTMLElement|Component} - Get/set the inner content of the veil.
     * @member shown {Boolean} - Get the 'shown' state of the veil.
     * @member unclosable {Boolean} - Get/set the 'unclosable' state of the veil (if the user can close via a
     * close button or not).
     * @member transparent {Boolean} - Get/set the 'transparent' state of the veil's background.
     * @member darkTheme {Boolean} - Get/set the the 'dark theme' of the veil.
     * @member topPosition {Number} - Get/set on override for the top position of the veil.
     */

    /**
     * Create a model for the veil.
     * @param [configOptions.title] {String|HTMLElement} - Title.
     * @param [configOptions.content] {String|HTMLElement|Component} - Content.
     * @param [configOptions.shown] {Boolean} - Shown. If true, shown immediately.
     * @param [configOptions.unclosable] {Boolean} - Unclosable.
     * @param [configOptions.transparent] {Boolean} - Transparent.
     * @param [configOptions.darkTheme] {Boolean} - Dark Theme.
     * @param [configOptions.topPosition] {Number|String} - Top position override.
     *
     */
    constructor(configOptions) {
        super();
        // Used to broadcasting shown/hidden events by the component and as a duration for animation by the renderer
        this._ANIMATION_DURATION = 333;
        this._title = _.get(configOptions, "title", "");
        this._content = _.get(configOptions, "content", "");
        this._shown = _.get(configOptions, "shown", false);
        this._unclosable = _.get(configOptions, "unclosable", false);
        this._transparent = _.get(configOptions, "transparent", true);
        this._darkTheme = _.get(configOptions, "darkTheme", true);
        this._topPosition = 0;
        this.setTopPosition(_.get(configOptions, "topPosition", 0));
        this._renderer = new Renderer(this);
        this._registerListeners();
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS */
    /*******************************************/

    _registerListeners() {}

    _unregisterListeners() {
        this.removeAllListeners();
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS */
    /* START PUBLIC COMPONENT MODEL METHODS */
    /*******************************************/

    /**
     * Returns the renderer associated with this control
     * @returns {LocalVeilGLRenderer}
     */
    getRenderer() {
        return this._renderer;
    }
    get renderer() {
        return this.getRenderer();
    }

    /**
     * The public shutdown function that returns a promise that is resolved when
     * the renderer has completed its shutdown routine.
     * @returns {Promise}
     */
    shutdown() {
        this._unregisterListeners();
        return this._renderer
            .shutdown()
            .then(() => {
                let prom = Promise.resolve();
                if (
                    this._content &&
                    !_.isUndefined(this._content.renderer) &&
                    !_.isUndefined(this._content.shutdown)
                ) {
                    prom = this._content.shutdown();
                }
                return prom;
            })
            .then(() => {
                this._title = undefined;
                this._content = undefined;
                this._shown = undefined;
                this._unclosable = undefined;
                this._transparent = undefined;
                this._darkTheme = undefined;
                this._topPosition = undefined;
                this._renderer = undefined;
            })
            .catch((err) => {
                console.error({error: err}, "LocalVeilGLComponent.shutdown() error.");
            });
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE METHODS */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE METHODS */
    /* START PUBLIC API METHODS */
    /*******************************************/

    /**
     * Sets the visible title of the veil.
     * @param newTitle {String|HTMLElement}
     * @returns {LocalVeilGLComponent}
     */
    setTitle(newTitle) {
        this._title = newTitle;
        this.emit("UpdateTitle", this._title);
        return this;
    }
    /**
     * Returns the title.
     * @returns {String|HTMLElement}
     */
    getTitle() {
        return this._title;
    }
    set title(newTitle) {
        this.setTitle(newTitle);
    }
    get title() {
        return this.getTitle();
    }

    /**
     * Sets the inner content of the veil.
     * @param newContent - String|HTMLElement|Component
     * @param {object} [opts] - Optional set properties.
     * @param {boolean} [opts.dontDestroy=false] - If truthy will NOT destory a component when it is being swapped.
     * @returns {LocalVeilGLComponent}
     */
    setContent(newContent, opts) {
        // If the current content is a component, shut it down before adding new content
        if (
            !_.get(opts, "dontDestroy") &&
            this._content &&
            !_.isUndefined(this._content.renderer) &&
            !_.isUndefined(this._content.shutdown)
        ) {
            this._content.shutdown().then(() => {
                this._content = newContent;
                this.emit("UpdateContent", this._content);
                return this;
            });
        } else {
            this._content = newContent;
            this.emit("UpdateContent", this._content);
            return this;
        }
    }
    /**
     * Returns the content.
     * @returns {String|HTMLElement}
     */
    getContent() {
        return this._content;
    }
    set content(newContent) {
        this.setContent(newContent);
    }
    get content() {
        return this.getContent();
    }

    /**
     * Returns the shown state.
     * @returns {Boolean}
     */
    getShown() {
        return this._shown;
    }
    /**
     * Shown state check.
     * @returns {Boolean}
     */
    isShown() {
        return this.getShown();
    }
    get shown() {
        return this.getShown();
    }

    /**
     * Toggles the unclosable state of the veil by removing the close button. Closing the veil with the
     * escape key still works, though. The veil is NOT unclosable by default.
     * @param newState - Boolean
     * @returns {LocalVeilGLComponent}
     */
    setUnclosable(newState) {
        this._unclosable = newState;
        this.emit("UpdateUnclosableState", this._unclosable);
        return this;
    }
    /**
     * Returns the unclosable state.
     * @returns {Boolean}
     */
    getUnclosable() {
        return this._unclosable;
    }
    /**
     * Unclosable state check.
     * @returns {Boolean}
     */
    isUnclosable() {
        return this.getUnclosable();
    }
    set unclosable(newState) {
        this.setUnclosable(newState);
    }
    get unclosable() {
        return this.getUnclosable();
    }

    /**
     * Toggles the transparent state of the veil's background.  Transparent is default.
     * @param newState - Boolean
     * @returns {LocalVeilGLComponent}
     */
    setTransparent(newState) {
        this._transparent = newState;
        this.emit("UpdateTransparentState", this._transparent);
        return this;
    }
    /**
     * Returns the transparent state of the background.
     * @returns {Boolean}
     */
    getTransparent() {
        return this._transparent;
    }
    /**
     * Transparent state check.
     * @returns {Boolean}
     */
    isTransparent() {
        return this.getTransparent();
    }
    set transparent(newState) {
        this.setTransparent(newState);
    }
    get transparent() {
        return this.getTransparent();
    }

    /**
     * Toggles the dark theme.  Dark is default.
     * @param newState - Boolean
     * @returns {LocalVeilGLComponent}
     */
    setDarkTheme(newState) {
        this._darkTheme = newState;
        this.emit("UpdateDarkTheme", this._darkTheme);
        return this;
    }
    /**
     * Returns the dark theme state.
     * @returns {Boolean}
     */
    getDarkTheme() {
        return this._darkTheme;
    }
    /**
     * Dark theme state check.
     * @returns {Boolean}
     */
    isDarkTheme() {
        return this.getDarkTheme();
    }
    set darkTheme(newState) {
        this.setDarkTheme(newState);
    }
    get darkTheme() {
        return this.getDarkTheme();
    }

    /**
     * Sets an override for the top position of the veil.
     * @param newState - Number|String
     * @returns {LocalVeilGLComponent}
     */
    setTopPosition(newState) {
        if (typeof newState === "string") {
            this._topPosition = parseFloat(newState);
        } else {
            this._topPosition = newState;
        }
        this.emit("UpdateTopPosition", this._topPosition);
        return this;
    }
    /**
     * Returns the overridden top position.
     * @returns {Number}
     */
    getTopPosition() {
        return this._topPosition;
    }
    set topPosition(newState) {
        this.setTopPosition(newState);
    }
    get topPosition() {
        return this.getTopPosition();
    }

    /**
     * Grows the veil into view.
     */
    show() {
        this.emit("Show");
        return this.renderer.show().then(() => {
            this.emit("ShowComplete");
        });
    }

    /**
     * Shrinks the veil out of view.
     */
    hide() {
        this.emit("Hide");
        return this.renderer.hide().then(() => {
            this.emit("HideComplete");
        });
    }

    /*******************************************/
    /* END PUBLIC API METHODS */
    /*******************************************/
}

module.exports = LocalVeilGLComponent;
